import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Inbox, ArrowRight, Trash2 } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { useOpenTask } from '@/features/tasks/public';
import { SubmissionDetailDialog } from './SubmissionDetailDialog';
import { useDeleteSubmission } from '../../hooks/useSubmissionMutations';
import type { FormField, Submission } from '../../types';

interface SubmissionsTableProps {
  formId: string;
  fields: FormField[];
  submissions: Submission[];
  isLoading: boolean;
  canCreateTask: boolean;
}

export function SubmissionsTable({
  formId,
  fields,
  submissions,
  isLoading,
  canCreateTask,
}: SubmissionsTableProps) {
  const { t } = useTranslation('forms');
  const openTask = useOpenTask();
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const deleteSubmission = useDeleteSubmission(formId);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  async function handleDelete(submissionId: string) {
    const { confirmed } = await confirm({
      title: t('submissions.confirmDeleteTitle'),
      description: t('submissions.confirmDeleteDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await deleteSubmission.mutateAsync(submissionId);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  if (!submissions.length) {
    return (
      <div className="text-center py-10">
        <Inbox className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t('submissions.empty')}</p>
      </div>
    );
  }

  const columns: ColumnDef<Submission, unknown>[] = [
    {
      id: 'respondent',
      accessorFn: (s) => s.submittedByEmail ?? '',
      header: t('submissions.table.respondent'),
      meta: { cellClassName: 'flex-[2] min-w-0' },
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => setSelectedSubmissionId(row.original.id)}
          className="truncate text-left text-sm hover:underline"
          data-testid={`forms-submissions-row-${row.original.id}`}
        >
          {row.original.isAnonymous ? (
            <Badge variant="outline">{t('submissions.table.anonymous')}</Badge>
          ) : (
            row.original.submittedByEmail
          )}
        </button>
      ),
    },
    {
      id: 'task',
      accessorFn: (s) => s.taskId ?? '',
      header: t('submissions.table.task'),
      meta: { cellClassName: 'flex-1 min-w-0' },
      cell: ({ row }) =>
        row.original.taskId ? (
          <button
            type="button"
            onClick={() => openTask(row.original.taskId as string)}
            className="flex items-center gap-1 text-sm text-primary hover:underline"
            data-testid={`forms-submissions-view-task-${row.original.id}`}
          >
            {t('submissions.detail.viewTask')}
            <ArrowRight className="h-3 w-3" />
          </button>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'date',
      accessorFn: (s) => s.createdAt,
      header: t('submissions.table.date'),
      meta: { cellClassName: 'flex-none w-44' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: undefined,
      meta: { cellClassName: 'flex-none w-12 flex justify-end' },
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(row.original.id)}
              disabled={deleteSubmission.isPending}
              aria-label={t('submissions.table.delete')}
              data-testid={`forms-submissions-delete-${row.original.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('submissions.table.delete')}</TooltipContent>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <ListView
        data={submissions}
        columns={columns}
        getRowId={(s) => s.id}
        data-testid="forms-submissions-table"
      />

      <SubmissionDetailDialog
        formId={formId}
        submissionId={selectedSubmissionId}
        fields={fields}
        canCreateTask={canCreateTask}
        onOpenChange={(open) => !open && setSelectedSubmissionId(null)}
      />

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(v) => {
          if (!v) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        variant={options?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
